import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  Loader2,
  RefreshCcw,
  Search,
  Bell,
  BellOff,
  Send,
  History,
  CheckCircle2,
  XCircle,
  Clock,
  Gavel,
  UserCheck,
  MessageSquare,
  UserX,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi, getToken, API } from "@/lib/adminApi";

const COMPLETION_LABELS = {
  started: "Démarrée",
  in_progress: "En cours",
  completed: "Complétée",
  abandoned: "Abandonnée",
};

const COMPLETION_STYLES = {
  started: "text-white/70 border-white/15 bg-white/5",
  in_progress: "text-sky-300 border-sky-400/40 bg-sky-400/5",
  completed: "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
  abandoned: "text-rose-300 border-rose-400/40 bg-rose-400/5",
};

const STEP_LABELS = {
  0: "Non démarrée",
  1: "Informations personnelles",
  2: "Sélection du profil",
  3: "Questions du profil",
  4: "Déclarations",
  5: "Vérification d'identité",
  6: "Revue & soumission",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
};

export const ApplicationsView = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], funnel: [], by_completion_status: {}, total: 0, filtered: 0 });
  const [search, setSearch] = useState("");
  const [completionFilter, setCompletionFilter] = useState("all");
  const [stepFilter, setStepFilter] = useState("all");
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [notifLoading, setNotifLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [resendingAppId, setResendingAppId] = useState(null);
  const [decisionApp, setDecisionApp] = useState(null); // application row currently being decided
  const [decisionType, setDecisionType] = useState(null); // accepted | info_requested | rejected
  const [decisionMessage, setDecisionMessage] = useState("");
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (completionFilter && completionFilter !== "all") params.completion_status = completionFilter;
      if (stepFilter && stepFilter !== "all") params.step = stepFilter;
      const { data: d } = await adminApi.get("/admin/applications", { params });
      setData(d);
    } catch (err) {
      toast.error("Impossible de charger les candidatures.");
    } finally {
      setLoading(false);
    }
  }, [search, completionFilter, stepFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/settings");
      setNotifEnabled(!!data.founder_notification_enabled);
    } catch (err) {
      // ignore
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/email-log", {
        params: { limit: 200 },
      });
      setLogItems(data.items || []);
    } catch (err) {
      toast.error("Impossible de charger le journal des envois.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const toggleNotif = async () => {
    setNotifLoading(true);
    try {
      const { data } = await adminApi.patch("/admin/settings", {
        founder_notification_enabled: !notifEnabled,
      });
      setNotifEnabled(!!data.founder_notification_enabled);
      toast.success(
        data.founder_notification_enabled
          ? "Notifications au fondateur : activées."
          : "Notifications au fondateur : désactivées."
      );
    } catch (err) {
      toast.error("Impossible de mettre à jour le paramètre.");
    } finally {
      setNotifLoading(false);
    }
  };

  const handleResendNotif = async (row) => {
    setResendingAppId(row.id);
    try {
      await adminApi.post(`/admin/applications/${row.id}/resend-founder-notification`);
      toast.success("Notification envoyée au fondateur.");
      if (showLog) loadLog();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Envoi impossible.";
      toast.error(typeof detail === "string" ? detail : "Envoi impossible.");
    } finally {
      setResendingAppId(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = getToken();
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      if (completionFilter !== "all") qs.set("completion_status", completionFilter);
      if (stepFilter !== "all") qs.set("step", stepFilter);
      const url = `${API}/admin/applications.csv${qs.toString() ? `?${qs}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `somnium-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Export impossible.");
    }
  };

  const openDecisionDialog = (row, type) => {
    setDecisionApp(row);
    setDecisionType(type);
    setDecisionMessage("");
  };

  const closeDecisionDialog = () => {
    if (decisionSubmitting) return;
    setDecisionApp(null);
    setDecisionType(null);
    setDecisionMessage("");
  };

  const submitDecision = async () => {
    if (!decisionApp || !decisionType) return;
    if (decisionType === "info_requested" && !decisionMessage.trim()) {
      toast.error("Un message est requis pour indiquer ce qui manque.");
      return;
    }
    setDecisionSubmitting(true);
    try {
      const body = { decision: decisionType };
      if (decisionType === "info_requested") body.message = decisionMessage.trim();
      const { data } = await adminApi.post(
        `/admin/applications/${decisionApp.id}/decision`,
        body
      );
      const label =
        decisionType === "accepted"
          ? `Accepté · ${data.tp_number}`
          : decisionType === "info_requested"
          ? "Complément demandé au candidat"
          : "Candidature refusée · blocage 90 jours actif";
      toast.success(label);
      closeDecisionDialog();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Décision impossible.";
      toast.error(typeof detail === "string" ? detail : "Décision impossible.");
    } finally {
      setDecisionSubmitting(false);
    }
  };

  // Countdown helper (business days until review_deadline_at)
  const deadlineInfo = (row) => {
    const d = row.review_deadline_at || row.reviewDeadlineAt || null;
    if (!d) return null;
    const dl = new Date(d);
    if (isNaN(dl.getTime())) return null;
    const now = new Date();
    const ms = dl.getTime() - now.getTime();
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    return {
      date: dl,
      days,
      overdue: days < 0,
      urgent: days >= 0 && days <= 2,
      dateStr: dl.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }),
    };
  };

  const maxFunnelReached = useMemo(
    () => Math.max(1, ...(data.funnel || []).map((f) => f.reached)),
    [data.funnel]
  );

  return (
    <div data-testid="admin-applications-view">
      {/* Top action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-white">Candidatures</h2>
          <p className="text-xs text-white/50 mt-1 tracking-mid">
            Suivi d'avancement des inscriptions et journal des notifications au fondateur.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleNotif}
            disabled={notifLoading}
            data-testid="admin-notif-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border transition-colors ${
              notifEnabled
                ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
                : "border-white/15 text-white/60 hover:border-white/30"
            }`}
            title={
              notifEnabled
                ? "Un email est envoyé au fondateur à chaque inscription complétée."
                : "Les notifications au fondateur sont désactivées."
            }
          >
            {notifLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : notifEnabled ? (
              <Bell size={14} />
            ) : (
              <BellOff size={14} />
            )}
            {notifEnabled ? "Notifs activées" : "Notifs désactivées"}
          </button>
          <button
            type="button"
            onClick={() => { setShowLog((v) => !v); if (!showLog) loadLog(); }}
            data-testid="admin-notif-log-toggle"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border border-white/15 text-white/70 hover:border-white/30 hover:text-white transition-colors"
          >
            <History size={14} /> Journal des envois
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="admin-applications-refresh"
            className="p-2 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors"
            title="Rafraîchir"
          >
            <RefreshCcw size={14} />
          </button>
          <button
            type="button"
            onClick={handleExportCSV}
            data-testid="admin-applications-export"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-mid uppercase rounded border border-white/15 hover:border-[#e6c98a]/50 hover:text-[#e6c98a] text-white/80 transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Completion status stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {Object.entries(COMPLETION_LABELS).map(([k, l]) => (
          <div
            key={k}
            className={`rounded-md border px-4 py-3 ${COMPLETION_STYLES[k]}`}
            data-testid={`admin-completion-stat-${k}`}
          >
            <div className="text-[10px] tracking-luxe uppercase opacity-80">{l}</div>
            <div className="text-2xl font-serif-display mt-1">
              {data.by_completion_status?.[k] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Funnel */}
      <div className="border border-white/10 rounded-md bg-black/40 p-5 mb-8" data-testid="admin-funnel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-luxe uppercase text-white/45">Entonnoir d'inscription</div>
            <div className="text-white/90 text-sm mt-1">
              {data.total} candidature{data.total > 1 ? "s" : ""} au total
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {(data.funnel || []).map((f) => {
            const widthPct = Math.max(4, Math.round((f.reached / maxFunnelReached) * 100));
            return (
              <div key={f.step} data-testid={`admin-funnel-step-${f.step}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="text-white/80">
                    <span className="text-[#e6c98a] tracking-mid mr-2">Étape {f.step}</span>
                    {f.label}
                  </div>
                  <div className="text-white/60 flex items-center gap-3">
                    <span className="text-white/90">{f.reached}</span>
                    <span className="text-[10px] tracking-mid uppercase">
                      {f.conversion_from_previous}% depuis l&apos;étape précédente
                    </span>
                  </div>
                </div>
                <div className="h-2 rounded bg-white/[0.04] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#e6c98a] to-[#e6c98a]/40 transition-all duration-500"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Notification log drawer */}
      {showLog && (
        <div
          className="border border-white/10 rounded-md bg-black/40 p-5 mb-8"
          data-testid="admin-notif-log"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-luxe uppercase text-white/45">
              Journal des notifications au fondateur ({logItems.length})
            </div>
            <button
              type="button"
              onClick={loadLog}
              className="text-[10px] tracking-mid uppercase text-white/60 hover:text-white"
            >
              <RefreshCcw size={12} className="inline mr-1" /> Rafraîchir
            </button>
          </div>
          {logItems.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">
              Aucune notification enregistrée pour l&apos;instant.
            </div>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-y-auto">
              {logItems.map((it, idx) => {
                const Ic =
                  it.status === "sent"
                    ? CheckCircle2
                    : it.status === "failed"
                    ? XCircle
                    : Clock;
                const color =
                  it.status === "sent"
                    ? "text-emerald-300"
                    : it.status === "failed"
                    ? "text-rose-300"
                    : "text-white/60";
                return (
                  <li
                    key={idx}
                    className="text-xs text-white/70 flex items-start gap-2 py-1.5 border-b border-white/5 last:border-b-0"
                    data-testid={`admin-notif-log-row-${idx}`}
                  >
                    <Ic size={14} className={`mt-0.5 flex-shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`uppercase tracking-mid text-[10px] ${color}`}>
                          {it.status}
                        </span>
                        {it.kind && (
                          <span className="text-white/60 text-[10px] tracking-mid uppercase" title="Email kind">
                            {String(it.kind).replace(/_/g, " ")}
                          </span>
                        )}
                        {it.delivery_status && (
                          <span
                            className={`uppercase tracking-mid text-[10px] px-1.5 py-0.5 rounded ${
                              it.delivery_status === "delivered"
                                ? "bg-emerald-400/10 text-emerald-300"
                                : it.delivery_status === "bounced"
                                ? "bg-rose-400/10 text-rose-300"
                                : it.delivery_status === "complained"
                                ? "bg-amber-400/10 text-amber-300"
                                : "bg-white/5 text-white/60"
                            }`}
                          >
                            {it.delivery_status}
                          </span>
                        )}
                        <span className="text-white/50 text-[10px]">{fmtDate(it.created_at)}</span>
                        {it.locale && (
                          <span className="text-white/40 text-[10px] tracking-mid uppercase">{it.locale}</span>
                        )}
                        {it.reference_number && (
                          <span className="text-[#e6c98a] text-[10px] font-mono">
                            {it.reference_number}
                          </span>
                        )}
                      </div>
                      <div className="text-white/60 truncate">
                        {it.to}
                        {it.resend_id ? ` · id ${String(it.resend_id).slice(0, 12)}…` : ""}
                      </div>
                      {(it.error || it.delivery_error) && (
                        <div className="text-rose-300/80 mt-1 text-[11px] line-clamp-2">
                          {it.error || it.delivery_error}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <Input
            data-testid="admin-applications-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, référence…"
            className="luxe-input h-11 pl-10 pr-4"
          />
        </div>
        <Select value={completionFilter} onValueChange={setCompletionFilter}>
          <SelectTrigger
            data-testid="admin-applications-status-filter"
            className="luxe-input h-11 px-4 w-full md:w-[180px]"
          >
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-white/10 text-white">
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(COMPLETION_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stepFilter} onValueChange={setStepFilter}>
          <SelectTrigger
            data-testid="admin-applications-step-filter"
            className="luxe-input h-11 px-4 w-full md:w-[220px]"
          >
            <SelectValue placeholder="Étape" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-white/10 text-white">
            <SelectItem value="all">Toutes les étapes</SelectItem>
            {Object.entries(STEP_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{v} · {l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-3 text-[10px] tracking-luxe uppercase text-white/40">
        {data.filtered} résultat{data.filtered > 1 ? "s" : ""} sur {data.total}
      </div>

      {/* Table */}
      <div className="border border-white/10 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-[10px] tracking-luxe uppercase text-white/45">
                <th className="px-4 py-3 font-normal">Référence</th>
                <th className="px-4 py-3 font-normal">Candidat</th>
                <th className="px-4 py-3 font-normal">Profil</th>
                <th className="px-4 py-3 font-normal">Étape atteinte</th>
                <th className="px-4 py-3 font-normal">Statut</th>
                <th className="px-4 py-3 font-normal">Dernière activité</th>
                <th className="px-4 py-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-white/40">
                    <Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-white/40">
                    Aucune candidature.
                  </td>
                </tr>
              ) : (
                data.items.map((row) => {
                  const isCompleted = row.completion_status === "completed";
                  const resending = resendingAppId === row.id;
                  const dl = deadlineInfo(row);
                  const finalized = ["accepted", "rejected", "abandoned", "expired"].includes(row.status);
                  const hasTp = !!row.tp_number;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`admin-applications-row-${row.id}`}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="text-[#e6c98a] text-xs tracking-mid font-mono">
                          {row.reference_number || "—"}
                        </div>
                        {hasTp && (
                          <a
                            href={`${API}/passport/${row.tp_number}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-luxe uppercase bg-emerald-400/10 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-400/20"
                            data-testid={`admin-applications-tp-${row.id}`}
                            title="Open Trust Passport visual"
                          >
                            {row.tp_number}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-white text-sm">{row.full_name || "—"}</div>
                        <div className="text-white/40 text-xs mt-1 break-all">{row.email}</div>
                        {row.locale && (
                          <div className="mt-1 text-[10px] text-white/40 tracking-mid uppercase">
                            {row.locale}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-white/70 text-xs uppercase tracking-mid">
                          {(row.selected_profiles || []).join(", ") || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="inline-flex items-center gap-2 px-2 py-1 rounded border border-white/10 bg-white/5">
                          <span className="text-[#e6c98a] text-xs font-mono">{row.current_step}/6</span>
                          <span className="text-white/70 text-xs">{row.current_step_label}</span>
                        </div>
                        <div className="mt-1 h-1 rounded bg-white/[0.05] overflow-hidden w-40">
                          <div
                            className="h-full bg-[#e6c98a]/70"
                            style={{ width: `${(row.current_step / 6) * 100}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded border text-[10px] tracking-mid uppercase ${COMPLETION_STYLES[row.completion_status]}`}
                        >
                          {COMPLETION_LABELS[row.completion_status] || row.completion_status}
                        </span>
                        {row.status && !finalized && row.status !== row.completion_status && (
                          <div className="mt-1 text-[9px] text-white/40 tracking-mid uppercase">
                            {row.status}
                          </div>
                        )}
                        {dl && !finalized && isCompleted && (
                          <div
                            className={`mt-1 text-[10px] tracking-mid uppercase inline-flex items-center gap-1 ${
                              dl.overdue
                                ? "text-rose-300"
                                : dl.urgent
                                ? "text-amber-300"
                                : "text-white/50"
                            }`}
                            data-testid={`admin-applications-deadline-${row.id}`}
                            title={`Deadline: ${dl.dateStr}`}
                          >
                            <Clock size={11} />
                            {dl.overdue
                              ? `Retard · ${Math.abs(dl.days)}j`
                              : `J-${dl.days} · ${dl.dateStr}`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-white/60 text-xs">
                        {fmtDate(row.updated_at)}
                      </td>
                      <td className="px-4 py-4 align-top text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {isCompleted && !finalized && (
                            <>
                              <button
                                type="button"
                                onClick={() => openDecisionDialog(row, "accepted")}
                                data-testid={`admin-decision-accept-${row.id}`}
                                title="Accepter · génère un Trust Passport"
                                className="p-2 rounded border border-emerald-400/40 text-emerald-300 hover:text-black hover:bg-emerald-300 hover:border-emerald-300 transition-colors"
                              >
                                <UserCheck size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openDecisionDialog(row, "info_requested")}
                                data-testid={`admin-decision-info-${row.id}`}
                                title="Complément requis — écrire au candidat"
                                className="p-2 rounded border border-amber-400/40 text-amber-300 hover:text-black hover:bg-amber-300 hover:border-amber-300 transition-colors"
                              >
                                <MessageSquare size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openDecisionDialog(row, "rejected")}
                                data-testid={`admin-decision-reject-${row.id}`}
                                title="Refuser — blocage 90 jours"
                                className="p-2 rounded border border-rose-400/40 text-rose-300 hover:text-black hover:bg-rose-300 hover:border-rose-300 transition-colors"
                              >
                                <UserX size={14} />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => handleResendNotif(row)}
                            disabled={!isCompleted || resending}
                            title={
                              isCompleted
                                ? "Renvoyer la notification au fondateur"
                                : "Disponible uniquement pour les candidatures complétées"
                            }
                            data-testid={`admin-applications-resend-notif-${row.id}`}
                            className={`p-2 rounded border transition-colors ${
                              isCompleted
                                ? "border-[#e6c98a]/40 text-[#e6c98a] hover:text-black hover:bg-[#e6c98a] hover:border-[#e6c98a]"
                                : "border-white/10 text-white/25 cursor-not-allowed"
                            }`}
                          >
                            {resending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Decision dialog */}
      <AlertDialog open={!!decisionApp} onOpenChange={(o) => !o && closeDecisionDialog()}>
        <AlertDialogContent className="bg-neutral-950 border-white/10 text-white max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-xl flex items-center gap-2">
              <Gavel size={18} />
              {decisionType === "accepted" && "Accepter cette candidature ?"}
              {decisionType === "info_requested" && "Demander un complément"}
              {decisionType === "rejected" && "Refuser cette candidature ?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {decisionApp?.reference_number && (
                <span className="text-[#e6c98a] font-mono">{decisionApp.reference_number}</span>
              )}
              {decisionApp?.full_name && (
                <> · <span className="text-white/80">{decisionApp.full_name}</span></>
              )}
              {decisionApp?.email && (
                <> · <span className="text-white/60">{decisionApp.email}</span></>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {decisionType === "accepted" && (
            <div className="rounded border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm text-white/80 leading-relaxed">
              Un numéro <span className="text-emerald-300">Trust Passport</span> séquentiel (TP-XXXXXX) sera attribué et un email de bienvenue sera envoyé au candidat dans sa langue de contact ({decisionApp?.locale || "fr"}).
            </div>
          )}
          {decisionType === "info_requested" && (
            <div className="space-y-2">
              <div className="text-[10px] tracking-luxe uppercase text-white/50">
                Message au candidat — expliquer ce qui manque
              </div>
              <Textarea
                data-testid="admin-decision-info-message"
                value={decisionMessage}
                onChange={(e) => setDecisionMessage(e.target.value)}
                rows={4}
                placeholder="Par exemple : Merci de nous fournir un justificatif de domicile de moins de 3 mois."
                className="luxe-input"
              />
              <div className="text-[11px] text-white/40">
                Le dossier repasse en cours d&apos;examen dès que le candidat renvoie l&apos;élément manquant. Aucun blocage.
              </div>
            </div>
          )}
          {decisionType === "rejected" && (
            <div className="rounded border border-rose-400/30 bg-rose-400/5 p-4 text-sm text-white/80 leading-relaxed">
              Le candidat sera prévenu par email. Un <span className="text-rose-300">blocage de 90 jours</span> sera activé sur son email <em>et</em> son nom complet. Ce délai est strict.
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={decisionSubmitting}
              className="bg-transparent border-white/15 text-white hover:bg-white/5"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitDecision(); }}
              disabled={decisionSubmitting}
              data-testid="admin-decision-confirm"
              className={
                decisionType === "accepted"
                  ? "bg-emerald-500/90 hover:bg-emerald-500 text-black"
                  : decisionType === "rejected"
                  ? "bg-rose-500/90 hover:bg-rose-500 text-white"
                  : "bg-amber-400 hover:bg-amber-300 text-black"
              }
            >
              {decisionSubmitting ? (
                <><Loader2 size={14} className="animate-spin mr-2" /> Envoi…</>
              ) : decisionType === "accepted" ? (
                "Accepter · Attribuer TP"
              ) : decisionType === "info_requested" ? (
                "Envoyer la demande"
              ) : (
                "Refuser · Bloquer 90 jours"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
